import { createTheme } from '@mui/material/styles'

// SmartNyuki Brand Colors
const colors = {
  primary: '#F8B133',
  accent: '#f29100',
  white: '#fff',
  black: '#000',
  greyDark: '#242424',
  grey: '#606060',
  greyFilter: '#808080',
  greyMedium: '#b0b0b0',
  greyLight: '#cccccc',
  greyLightest: '#f1f1f1',
  offWhite: '#e9eae1',
  orangeMedium: '#ffedc7',
  orangeLight: '#fffaf0',
  orangeBorder: '#fff5e2',
  yellow: '#ffe900',
  green: '#049717',
  greenDark: '#27820e',
  blueDark: '#094da0',
  blue: '#09bdef',
  beige: '#ead49e',
  brown: '#b7701f',
  brownDark: '#754b1f',
  red: '#ff001d',
  redLight: '#ffe5e7',
  redDark: '#d80d0d',
}

export const theme = createTheme({
  palette: {
    primary: {
      main: colors.primary,
      contrastText: colors.black,
    },
    secondary: {
      main: colors.accent,
      contrastText: colors.white,
    },
    error: {
      main: colors.red,
      light: colors.redLight,
      dark: colors.redDark,
    },
    warning: {
      main: colors.yellow,
    },
    info: {
      main: colors.greyDark,
      dark: colors.black,
    },
    success: {
      main: colors.green,
      dark: colors.greenDark,
    },
    grey: {
      50: colors.greyLightest,
      100: colors.greyLight,
      200: colors.greyMedium,
      300: colors.greyFilter,
      400: colors.grey,
      500: colors.greyDark,
      600: colors.greyDark,
      700: colors.greyDark,
      800: colors.black,
      900: colors.black,
    },
    background: {
      default: colors.white,
      paper: colors.white,
    },
    text: {
      primary: colors.greyDark,
      secondary: colors.grey,
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '1.75rem',
      fontWeight: 500,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '1.4rem',
      fontWeight: 500,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.15rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '0.9rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: '0.8rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    body1: {
      fontSize: '0.9rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.8rem',
      lineHeight: 1.43,
    },
    caption: {
      fontSize: '0.7rem',
      lineHeight: 1.66,
    },
    overline: {
      fontSize: '0.7rem',
      fontWeight: 500,
      lineHeight: 2.66,
      textTransform: 'uppercase',
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  spacing: 8,
  shape: {
    borderRadius: 4,
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.black,
          color: colors.primary,
          boxShadow: 'none',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          backgroundColor: colors.primary,
          '& .MuiTabs-indicator': {
            backgroundColor: colors.black,
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: colors.black,
          '&.Mui-selected': {
            color: colors.black,
            fontWeight: 500,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 0,
        },
        outlined: {
          borderColor: colors.greyLight,
          '&:hover': {
            borderColor: colors.primary,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          borderRadius: 4,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '&.Mui-focused fieldset': {
              borderColor: colors.primary,
            },
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: colors.primary,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 5,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardError: {
          backgroundColor: colors.redLight,
          color: colors.redDark,
        },
        standardWarning: {
          backgroundColor: colors.orangeLight,
          color: colors.accent,
        },
        standardInfo: {
          backgroundColor: colors.orangeLight,
          color: colors.brownDark,
        },
        standardSuccess: {
          backgroundColor: colors.offWhite,
          color: colors.greenDark,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.white,
          borderLeft: `1px solid ${colors.greyLight}`,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: colors.orangeLight,
            '&:hover': {
              backgroundColor: colors.orangeMedium,
            },
          },
        },
      },
    },
  },
})

// Custom theme extensions
declare module '@mui/material/styles' {
  interface Palette {
    SmartNyuki: {
      orangeLight: string
      orangeMedium: string
      orangeBorder: string
      greyFilter: string
    }
  }

  interface PaletteOptions {
    SmartNyuki?: {
      orangeLight?: string
      orangeMedium?: string
      orangeBorder?: string
      greyFilter?: string
    }
  }
}

// Add custom colors to theme
theme.palette.SmartNyuki = {
  orangeLight: colors.orangeLight,
  orangeMedium: colors.orangeMedium,
  orangeBorder: colors.orangeBorder,
  greyFilter: colors.greyFilter,
}

export { colors }
